import React, { memo, useEffect, useMemo } from 'react'
import Form from 'components/Form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, Controller } from 'react-hook-form'
import GridItem from 'components/Grid/GridItem.js'
import GridContainer from 'components/Grid/GridContainer.js'
import CustomInput from 'components/CustomInput'
import BookingModel from 'models/Booking'
import CustomPhoneInput from 'components/CustomPhoneInput'
import CustomSelect from 'components/CustomSelect'
import { bookingStatuses } from 'constants/bookings'
import defaults from './defaults'
import yupHelper from 'utils/yupHelper'
import getProps from 'utils/getProps'
import { formatDateTime } from 'utils/formatDateTime'
import { fields, defaults as fieldDefaults } from 'utils/fields'
import { Typography } from '@material-ui/core'
import SlotTimetable from './SlotTimetable'
import deepmerge from 'deepmerge'
import { format } from 'utils/currency'
import getPaymentGateway from 'utils/getPaymentGateway'
import Button from 'components/CustomButtons/Button.js'
import { TYPE_OPEN } from 'constants/times'

const BookingForm = memo(
  ({
    title = 'Enter Details',
    slotTimetable,
    onSubmit,
    onDiscard,
    onBlock,
    showStatus = false,
    showSlotSelector = false,
    submitButtonText = 'Book',
    dirtyOnStart = true,
    defaultValues = defaults,
    cardStyle,
    customer = null,
    loadingAfterSubmitted = false,
    users = [],
    editable,
    settings,
    selectorRef,
    showSubTitle,
  }) => {
    let customerDefaults = deepmerge(fieldDefaults, defaultValues)
    if (customer) {
      customerDefaults = deepmerge(customerDefaults, customer)
    }

    // const isInitialMount = useRef()
    const hookForm = useForm({
      resolver: yupResolver(BookingModel.validationSchema.shape(...fields.map((field) => yupHelper(field)))),
      defaultValues: { ...customerDefaults },
    })
    const {
      formState: { errors },
      control,
      register,
      reset,
    } = hookForm

    const user = useMemo(
      () => users?.data?.find((user) => user.id === defaultValues.userId),
      [users?.data, defaultValues.userId]
    )

    useEffect(() => {
      // if (isInitialMount.current) {
      //   isInitialMount.current = false
      // } else {
      reset({ ...customerDefaults })
      // }
    }, [defaultValues, customer])

    console.log(users, defaultValues)

    return (
      <Form
        title={title}
        subTitle={showSubTitle && slotTimetable?.displayTime()}
        submitButtonText={submitButtonText}
        dirtyOnStart={dirtyOnStart}
        hookForm={hookForm}
        onSubmit={onSubmit}
        onDiscard={onDiscard}
        cardStyle={cardStyle}
        customButtons={
          <>
            {onBlock && (
              <Button color="secondary" onClick={onBlock}>
                {slotTimetable?.type === TYPE_OPEN ? 'Block' : 'Unblock'}
              </Button>
            )}
          </>
        }
        defaultValues={customerDefaults}
        loadingAfterSubmitted={loadingAfterSubmitted}
      >
        {showSlotSelector && settings && (
          <GridContainer>
            <GridItem xs={12} sm={12} md={12}>
              <Controller
                control={control}
                name="slotTimetable"
                render={({ field }) => (
                  <SlotTimetable
                    {...field}
                    settings={settings}
                    defaultDate={slotTimetable.start}
                    slotId={slotTimetable.slotId}
                    selectedSlotTimetable={slotTimetable}
                    selectorRef={selectorRef}
                  />
                )}
              />
            </GridItem>
          </GridContainer>
        )}
        {showSlotSelector && (
          <GridContainer>
            <GridItem xs={12} sm={12} md={12}>
              <Typography>
                Booked For: <b>{defaultValues.slot?.name}</b>
              </Typography>
            </GridItem>
            {defaultValues?.paidAmount?.amount && (
              <GridItem xs={12} sm={12} md={12}>
                <Typography>
                  Paid Amount: <b>{format(defaultValues?.paidAmount?.amount)}</b>
                </Typography>
              </GridItem>
            )}
            {defaultValues?.paidAmount?.gateway && (
              <GridItem xs={12} sm={12} md={12}>
                <Typography>
                  Gateway:{' '}
                  <b>
                    {getPaymentGateway(defaultValues?.paidAmount?.gateway, defaultValues?.paidAmount?.method)?.name}
                  </b>
                </Typography>
              </GridItem>
            )}
            {defaultValues?.paidAmount?.transactionId && (
              <GridItem xs={12} sm={12} md={12}>
                <Typography>
                  Transaction Id: <b>{defaultValues?.paidAmount?.transactionId}</b>
                </Typography>
              </GridItem>
            )}
            <GridItem xs={12} sm={12} md={12}>
              <Typography>Booked Made At: {formatDateTime(defaultValues.createdAt)}</Typography>
            </GridItem>
            {user && (
              <GridItem xs={12} sm={12} md={12}>
                <Typography>Booked Made By: {user?.name}</Typography>
              </GridItem>
            )}
          </GridContainer>
        )}
        {showStatus ? (
          <GridContainer>
            <GridItem xs={12} sm={12} md={12}>
              <Controller
                control={control}
                name="status"
                render={({ field }) => (
                  <CustomSelect {...field} label="Status" options={bookingStatuses} error={errors.status} fullWidth />
                )}
              />
            </GridItem>
          </GridContainer>
        ) : (
          <input type="hidden" {...register('status')} />
        )}
        <GridContainer>
          <GridItem xs={12} sm={12} md={12}>
            <Controller
              control={control}
              name="name"
              render={({ field }) => (
                <CustomInput {...field} label="Name" error={errors.name} disabled={!editable} fullWidth />
              )}
            />
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={12} sm={12} md={12}>
            <Controller
              control={control}
              name="email"
              render={({ field }) => (
                <CustomInput
                  {...field}
                  type="email"
                  label="Email"
                  error={errors.email || errors.emailOrPhone}
                  disabled={!editable}
                  fullWidth
                />
              )}
            />
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={12} sm={12} md={12}>
            <Controller
              control={control}
              fullWidth={true}
              name="phone"
              render={({ field }) => (
                <CustomPhoneInput
                  {...field}
                  type="phone"
                  label="Phone Number"
                  error={errors.phone || errors.emailOrPhone}
                  disabled={!editable}
                  fullWidth
                />
              )}
            />
          </GridItem>
        </GridContainer>
        {fields.map(({ name, ...props }) => (
          <GridContainer key={name}>
            <GridItem xs={12} sm={12} md={12}>
              <Controller
                control={control}
                name={name}
                render={({ field }) => (
                  <CustomInput {...field} {...props} error={getProps(errors, name)} disabled={!editable} fullWidth />
                )}
              />
            </GridItem>
          </GridContainer>
        ))}
      </Form>
    )
  }
)

export default BookingForm
